import { useEffect, useState } from 'react';
import { ApiError, type ListInput } from '../../api/client';
import { text } from '../../app/i18n';
import { CommandDialog } from '../CommandDialog';
import { useAppServices, type ViewCommand } from '../ViewContext';
import { bulkAvailability, type BulkActionSpec } from './bulk-actions';
import type { Selection } from './selection';
import { SelectionToolbar } from './SelectionToolbar';

/**
 * Barre de sélection et actions de masse (ADR-096 §4 ; ADR-029 ; ADR-149 §3). Une action de masse
 * est une **opération du Work Engine** (`StartBulkOperation`, effet `RunCommand`) : la commande
 * déclarée est exécutée pour chaque enregistrement et ré-autorisée à chaque fois ; jamais une boucle
 * du navigateur. « Sélectionner les N » vise tout l'ensemble filtré, figé au lancement. Les bornes
 * déclarées grisent l'action d'avance ; la saisie de la commande vaut confirmation.
 */

type Progress = { state: string; total: number; processed: number; succeeded: number; failed: number; skipped: number };

const FINISHED = new Set(['completed', 'completed_with_errors', 'failed', 'cancelled', 'previewed']);
const POLL_MS = 1000;

export interface BulkAction {
  command: ViewCommand;
  spec: BulkActionSpec;
}

export function BulkBar({
  type,
  selection,
  total,
  loadedCount,
  actions,
  query,
  onFinished,
}: {
  type: string;
  selection: Selection;
  /** Taille de l'ensemble filtré (compteur du serveur). */
  total: number | undefined;
  loadedCount: number;
  /** Actions de masse déclarées par la vue, avec la commande servie par `GetView`. */
  actions: readonly BulkAction[];
  /** Requête gouvernée de la liste, pour « tout l'ensemble filtré ». */
  query: Pick<ListInput, 'filter' | 'q' | 'q_fields'>;
  onFinished: () => void;
}) {
  const services = useAppServices();
  const [chosen, setChosen] = useState<BulkAction | null>(null);
  const [operation, setOperation] = useState<{ id: string; label: string; progress?: Progress } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Suivi de l'opération : sa projection `BulkOperation`, relue jusqu'à la fin.
  useEffect(() => {
    if (operation === null || (operation.progress !== undefined && FINISHED.has(operation.progress.state))) return;
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const read = await services.client.getObject<{ state?: string; progress?: Omit<Progress, 'state'> }>('BulkOperation', operation.id);
          const progress = { state: read.data.state ?? 'running', total: read.data.progress?.total ?? 0, processed: read.data.progress?.processed ?? 0, succeeded: read.data.progress?.succeeded ?? 0, failed: read.data.progress?.failed ?? 0, skipped: read.data.progress?.skipped ?? 0 };
          setOperation((current) => (current === null || current.id !== operation.id ? current : { ...current, progress }));
          if (FINISHED.has(progress.state)) onFinished();
        } catch (caught) {
          setError(caught instanceof ApiError ? `Suivi indisponible : ${caught.message}` : String(caught));
        }
      })();
    }, POLL_MS);
    return () => clearTimeout(timer);
  }, [operation, services.client, onFinished]);

  if (selection.count === 0 && operation === null) return null;

  const launch = async (command: ViewCommand, payload: Record<string, unknown>): Promise<void> => {
    const selected = selection.mode === 'all' ? { list: { ...(query.filter === undefined || Object.keys(query.filter).length === 0 ? {} : { filter: query.filter }), ...(query.q === undefined ? {} : { q: query.q }), ...(query.q === undefined || query.q_fields === undefined ? {} : { q_fields: [...query.q_fields] }) } } : { ids: [...selection.ids] };
    const response = await services.client.command<{ result?: { bulk_operation_id?: string; estimated_count?: number } }>('StartBulkOperation', {
      payload: { operation_type: 'RunCommand', target_object: type, mode: 'execute', selection: selected, chunk_size: 50, parameters: { command_name: command.command_name, payload } },
    });
    const id = response.result?.bulk_operation_id;
    selection.clear();
    setChosen(null);
    setError(null);
    if (id !== undefined) setOperation({ id, label: text(command.label) });
    else onFinished();
  };

  const count = selection.count;
  const everything = total !== undefined && total > loadedCount && selection.mode === 'ids' && selection.ids.size === loadedCount;
  return (
    <>
      {operation !== null ? (
        <div className="ay-selbar" role="region" aria-label="Sélection">
          <p role="status" className="ay-bulkbar__status">
            {operation.label} :{' '}
            {operation.progress === undefined
              ? 'opération lancée…'
              : `${operation.progress.processed.toLocaleString('fr')} / ${operation.progress.total.toLocaleString('fr')} traités — ${operation.progress.succeeded.toLocaleString('fr')} réussis, ${operation.progress.failed.toLocaleString('fr')} en échec, ${operation.progress.skipped.toLocaleString('fr')} ignorés${FINISHED.has(operation.progress.state) ? ' (terminé)' : ''}`}
            {operation.progress !== undefined && FINISHED.has(operation.progress.state) && (
              <button type="button" className="ay-button ay-button--secondary" onClick={() => setOperation(null)}>
                Fermer
              </button>
            )}
          </p>
        </div>
      ) : (
        <SelectionToolbar
          label={`${count.toLocaleString('fr')} sélectionné${count > 1 ? 's' : ''}${selection.mode === 'all' ? ' (tout l’ensemble filtré)' : ''}`}
          everything={everything ? { label: `Sélectionner les ${total!.toLocaleString('fr')}`, onSelect: selection.selectAll } : undefined}
          onClear={selection.clear}
          actions={actions.map((action) => {
            const available = bulkAvailability(action.spec, count);
            return {
              id: action.command.command_name,
              label: action.spec.label ?? text(action.command.label),
              ...(action.spec.icon === undefined ? {} : { icon: action.spec.icon }),
              danger: action.spec.danger,
              disabled: !available.enabled,
              ...(available.reason === undefined ? {} : { reason: available.reason }),
              // La saisie de la commande (CommandDialog) vaut confirmation : elle dit combien d'éléments.
              onRun: () => setChosen(action),
            };
          })}
        >
          {error !== null && (
            <span role="alert" className="ay-banner ay-banner--danger">
              {error}
            </span>
          )}
        </SelectionToolbar>
      )}
      {chosen !== null && (
        <CommandDialog
          command={chosen.command}
          prefilled={{}}
          target={null}
          intro={
            <p className={`ay-banner ${chosen.spec.danger ? 'ay-banner--danger' : 'ay-banner--warning'}`} role="note">
              {chosen.spec.confirm?.message !== undefined ? `${chosen.spec.confirm.message} ` : ''}« {chosen.spec.label ?? text(chosen.command.label)} » sera exécutée sur {count.toLocaleString('fr')} élément{count > 1 ? 's' : ''}, une commande par élément, chacune ré-autorisée. Un refus n’arrête pas les autres.
            </p>
          }
          onSubmitPayload={(payload) => launch(chosen.command, payload)}
          onDone={() => undefined}
          onClose={() => setChosen(null)}
        />
      )}
    </>
  );
}
